<template>
  <div class="grid grid-rows-1 justify-items-center">
    <div class="lg:w-5/6 xl:w-3/6 bg-white rounded-lg -mt-6">
      <!-- Header always visible -->
      <div class="flex justify-between">
        <h1 class="text-3xl fontLight mb-6 text-center w-full">
          {{ observationDetailContent?.title }}
          <span class="cursor-pointer underline" @click="handleObservationClick">Observasjon</span>
        </h1>
        <span class="cursor-pointer text-xl" @click="closeDetails">X</span>
      </div>

      <!-- Content (switches via if/else chain) -->
      <div>
        <!-- Overview / landing content -->
        <div
          v-if="!showPlan && !showTheory && !showForm && !showLearn && !showReflex && !showGuidance"
          class="grid grid-rows-1 justify-items-center"
        >
          <div class="mb-8">
            <p class="text-base font text-center w-full" v-html="observationDetailContent?.content"></p>
          </div>

          <div class="my-24">
            <img
              :src="observationDetailContent?.image || observationImage"
              alt="Observation Tool details Image"
              class="max-w-full h-auto rounded-lg"
            />
          </div>
        </div>

        <!-- Existing pages -->
        <ObservationPlanItem
          v-else-if="showPlan"
          :mode="mode"
          @close-popup="closeDetails"
          @switch-component="handleSwitchComponent"
        />

        <ObservationTheory
          v-else-if="showTheory"
          :mode="mode"
          @close-popup="closeDetails"
          @switch-component="handleSwitchComponent"
        />

        <ObservationFormItem v-else-if="showForm" :mode="mode" @close-popup="closeDetails" />

        <ObservationLearn v-else-if="showLearn" :mode="mode" @close-popup="closeDetails" />

        <IndividualReflex v-else-if="showReflex" :mode="mode" @close-popup="closeDetails" />

        <GuidanceItem v-else-if="showGuidance" :mode="mode" @close-popup="closeDetails" />
      </div>

      <!-- Process nav: Back | Menu | Next -->
      <div class="flex items-center justify-between mt-12 mb-12 gap-3">
        <!-- Left: Back -->
        <div class="w-28">
          <SlButton
            v-if="!isFirst"
            class="buttonbackgroundColor h-8 w-28 focus:outline-none btn_text text-white text-sm"
            :custom-width="'8rem'"
            @click="backButton"
          >
            Tilbake
          </SlButton>
        </div>

        <!-- Middle: Menu / step indicator -->
        <div class="flex-1 flex justify-center">
          <div class="">
            <obsToolMenu v-model="selected" :items="menuItems" />
          </div>
        </div>

        <!-- Right: Next -->
        <div class="w-28 flex justify-end">
          <SlButton
            v-if="!isLast"
            class="buttonbackgroundColor h-8 w-28 focus:outline-none btn_text text-white text-sm"
            :custom-width="'8rem'"
            @click="nextButton"
          >
            Neste
          </SlButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, computed, onMounted, ref, watch } from 'vue'
  import obsToolMenu, { type obsMenuKey } from '@/components/obsToolMenu.vue'
  import { useCMSStore } from '@/store/useCMSStore'
  import { useUserStore } from '@/store/useUserStore'
  import observationImage from '@/assets/observation.svg'
  import SlButton from '@/components/base/SlButton.vue'
  import ObservationPlanItem from '@/components/ObservationPlanItem.vue'
  import ObservationTheory from '@/components/ObservationDetails.vue'
  import ObsToolMenu from '@/components/obsToolMenu.vue'
  import GuidanceItem from './GuidanceItem.vue'
  import IndividualReflex from './IndividualReflex.vue'
  import ObservationLearn from './ObservationLearn.vue'
  import ObservationFormItem from './ObservationFormItem.vue'

  export default defineComponent({
    name: 'ObservationToolDetails',
    components: {
      SlButton,
      ObservationPlanItem,
      ObservationTheory,
      obsToolMenu,
      GuidanceItem,
      IndividualReflex,
      ObservationLearn,
      ObservationFormItem,
    },
    props: {
      mode: { type: String, required: true },
      title: { type: Object, required: false },
      class: { type: String, required: false },
      menuSelection: { type: String, required: false, default: 'intro' },
    },
    emits: ['select', 'close-popup', 'switch-component'],
    setup(props, { emit }) {
      //Setup menu for observation tool:
      const selected = ref<obsMenuKey>(props.menuSelection as obsMenuKey)
      const previousSelected = ref<obsMenuKey>('plan')
      const order: obsMenuKey[] = ['intro', 'plan', 'form', 'learn', 'reflex', 'guidance']

      //Menu for observation tool
      const menuItems: { key: obsMenuKey; label: string }[] = [
        { key: 'intro', label: 'Introduksjon' },
        { key: 'plan', label: 'Planlegging' },
        { key: 'form', label: 'Observasjonsskjema' },
        { key: 'learn', label: 'Observasjon' },
        { key: 'reflex', label: 'Refleksjon' },
        { key: 'guidance', label: 'Etterveiledning' },
      ]

      const { getters: cmsGetters, actions: cmsActions } = useCMSStore()
      const { actions: userActions } = useUserStore()

      onMounted(() => {
        cmsActions.getObservationDetailContents()
      })

      const showTheory = ref(false)
      const observationDetailContent = computed(() => cmsGetters.observationDetail.value)

      const closeDetails = () => {
        emit('close-popup')
      }

      const handleObservationClick = () => {
        showTheory.value = true
        showPlan.value = false
      }

      const handleSwitchComponent = (component: string) => {
        if (component === 'detail') {
          showPlan.value = false
          showTheory.value = false
        }
      }

      const showPlan = ref(false)

      //New bools for the Observation tool menu
      const showForm = ref(false)
      const showLearn = ref(false)
      const showReflex = ref(false)
      const showGuidance = ref(false)

      //Observation tool menu reset variables
      const resetPages = () => {
        showPlan.value = false
        showTheory.value = false
        showForm.value = false
        showLearn.value = false
        showReflex.value = false
        showGuidance.value = false
      }

      //Used to hide back/next button if standing on the first/last page in the sequence
      const isFirst = computed(() => selected.value === order[0])
      const isLast = computed(() => selected.value === order[order.length - 1])

      //Watch observation tool menu changes, need to watch menu selections
      watch(
        selected,
        async (k, prev) => {
          previousSelected.value = prev ?? 'plan' //Default to first observation tool item/page
          resetPages()

          if (k === 'plan') {
            await cmsActions.getObservationPlanContents()
            showPlan.value = true
          } else if (k === 'form') {
            showForm.value = true
          } else if (k === 'learn') {
            showLearn.value = true
          } else if (k === 'reflex') {
            showReflex.value = true
          } else if (k === 'guidance') {
            showGuidance.value = true
          }
        },
        { immediate: true },
      )

      const nextContent = async () => {
        await cmsActions.getObservationPlanContents()
        if (cmsGetters.observationPlan.value) {
          showPlan.value = true
        }
      }

      //New function for back button that goes back to the previous item in the sequence
      const backButton = () => {
        const i = order.indexOf(selected.value)
        if (i <= 0) return // already at 'plan'
        selected.value = order[i - 1]
      }

      //Also a new function for next button that navigates to the next item in secuence
      const nextButton = () => {
        const i = order.indexOf(selected.value)
        if (i === -1 || i >= order.length - 1) return
        selected.value = order[i + 1]
      }

      return {
        observationDetailContent,
        closeDetails,
        handleSwitchComponent,
        observationImage,
        SlButton,
        nextContent,
        showPlan,
        handleObservationClick,
        showTheory,
        selected,
        previousSelected,
        menuItems,
        showForm,
        showLearn,
        showReflex,
        showGuidance,
        nextButton,
        backButton,
        isFirst,
        isLast,
      }
    },
  })
</script>

<style scoped>
  .fontLight {
    letter-spacing: 0px;
    font-weight: 300;
    font-family: 'Inter-Light', 'Inter', sans-serif;
  }

  .font {
    letter-spacing: 0px;
    font-weight: 300;
    font-family: 'Inter', sans-serif;
  }

  .btn_text {
    font-family: 'Inter-Medium', 'Inter', sans-serif;
  }

  .buttonbackgroundColor {
    background-color: var(--Canvas_Bluelight, #1a6899);
  }

  img {
    display: block;
    margin-left: auto;
    margin-right: auto;
  }
</style>
